import React, {
  useState, useCallback, useEffect, useRef, 
} from 'react';
import {
  Card,
  Typography,
  Upload,
  Button,
  Select,
  Input,
  message,
  Progress,
  Steps,
  Alert,
  Divider,
  Row,
  Col,
  Spin,
} from 'antd';
import NoSleep from 'nosleep.js';
import useLogin from '../../hooks/useLogin';
import BulkImportApi from '../../services/bulkImportApi';
import {
  combine, parseEvents, parseSessions, parseModules, parseOptions, parseFileWithWarnings,
} from '../../services/bulkImportParser';
import { fetchGoogleSheetData } from '../../services/googleSheetsService';
// import moment if available
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

function delay(time) {
  return new Promise((resolve) => {
    setTimeout(resolve, time);
  });
}

function BulkImportPage() {
  const { user } = useLogin();

  // NoSleep instance
  const noSleep = useRef(null);
  const [noSleepEnabled, setNoSleepEnabled] = useState(false);

  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const [importSource, setImportSource] = useState('google'); // 'file' or 'google'
  const [dateFormat, setDateFormat] = useState('auto');
  const [fileList, setFileList] = useState([]);
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importLog, setImportLog] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [archiveDomain, setArchiveDomain] = useState(true);
  const [domainsToArchive, setDomainsToArchive] = useState('');
  const [autoDomainsToArchive, setAutoDomainsToArchive] = useState('');
  const [reading, setReading] = useState(false);

  // Initialize NoSleep
  useEffect(() => {
    noSleep.current = new NoSleep();
    
    // Cleanup on unmount
    return () => {
      if (noSleep.current && noSleepEnabled) {
        try {
          noSleep.current.disable();
        } catch (error) {
          console.warn('Error disabling NoSleep:', error);
        }
      }
    };
  }, [noSleepEnabled]);

  // Date format options
  const dateFormatOptions = [
    { value: 'auto', label: 'Auto-detect' },
    { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY (American)' },
    { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY (European)' },
    { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD (ISO)' },
    { value: 'excel', label: 'Excel Date Format' },
  ];

  // Add log entry
  const addLog = useCallback((logMessage, type = 'info') => {
    setImportLog((prev) => [...prev, { message: logMessage, type, timestamp: new Date().toISOString() }]);
  }, []);

  // NoSleep management functions
  const enableNoSleep = useCallback(() => {
    if (noSleep.current && !noSleepEnabled) {
      try {
        noSleep.current.enable();
        setNoSleepEnabled(true);
        addLog('🔒 Screen wake lock enabled - your computer will not go to sleep during import', 'info');
      } catch (error) {
        console.warn('Error enabling NoSleep:', error);
        addLog('⚠️ Could not enable screen wake lock - please keep your computer active during import', 'warning');
      }
    }
  }, [noSleepEnabled, addLog]);

  const disableNoSleep = useCallback(() => {
    if (noSleep.current && noSleepEnabled) {
      try {
        noSleep.current.disable();
        setNoSleepEnabled(false);
        addLog('🔓 Screen wake lock disabled', 'info');
      } catch (error) {
        console.warn('Error disabling NoSleep:', error);
      }
    }
  }, [noSleepEnabled, addLog]);

  // Handle file upload
  const handleFileUpload = useCallback(async (file) => {
    try {
      addLog(`Processing file: ${file.name}`, 'info');

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = e.target.result;
          const result = parseFileWithWarnings(data);
          setParsedData(result.events);
          setPreviewData(result.events);
          addLog(`Successfully parsed ${result.events.length} events`, 'success');
          
          // Extract unique domain names from events
          const domains = Array.from(new Set(result.events.map((ev) => ev.domain).filter(Boolean)));
          setAutoDomainsToArchive(domains.join(', '));

          // Display warnings about untitled columns
          if (result.warnings && result.warnings.length > 0) {
            addLog(`Warning: ${result.warnings.length} untitled columns were skipped during parsing`, 'warning');
            result.warnings.forEach((warning) => {
              addLog(warning, 'warning');
            });
          }
          
          setCurrentStep(1);
        } catch (error) {
          addLog(`Error parsing file: ${error.message}`, 'error');
          message.error('Failed to parse file. Please check the file format.');
        }
      };
      reader.readAsBinaryString(file);
    } catch (error) {
      addLog(`Error reading file: ${error.message}`, 'error');
      message.error('Failed to read file.');
    }
  }, [addLog]);

  // Handle Google Sheets
  const handleGoogleSheets = useCallback(async () => {
    if (!googleSheetUrl) {
      message.error('Please enter a Google Sheets URL');
      return;
    }
    setReading(true);
    try {
      addLog('Connecting to Google Sheets...', 'info');
      // Define date columns that should be processed
      const dateColumns = ['start_date', 'end_date', 'schedule_start', 'schedule_end'];
      const response = await fetchGoogleSheetData(googleSheetUrl, {
        dateFormat,
        dateColumns,
        processDates: true,
      });
      const { sheets } = response;
      addLog('Parsing Google Sheets data...', 'info');
      
      const eventsResult = combine({
        events: parseEvents(sheets),
        sessions: parseSessions(sheets),
        modules: parseModules(sheets),
        options: parseOptions(sheets),
      });
      setParsedData(eventsResult.events);
      setPreviewData(eventsResult.events);
      addLog(`Successfully parsed ${eventsResult.events.length} events from Google Sheets`, 'success');
      // Extract unique domain names from events
      const domains = Array.from(new Set(eventsResult.events.map((ev) => ev.domain).filter(Boolean)));
      setAutoDomainsToArchive(domains.join(', '));
      // Display warnings about untitled columns
      if (eventsResult.warnings && eventsResult.warnings.length > 0) {
        addLog(`Warning: ${eventsResult.warnings.length} untitled columns were skipped during parsing`, 'warning');
        eventsResult.warnings.forEach((warning) => {
          addLog(warning, 'warning');
        });
      }
      setCurrentStep(1);
    } catch (error) {
      addLog(`Error processing Google Sheets: ${error.message}`, 'error');
      // Provide specific guidance for common service account issues
      if (error.message.includes('Service account does not have access')) {
        message.error('Access denied. Please share the spreadsheet with the service account email.');
      } else if (error.message.includes('credentials not configured')) {
        message.error('Service account not configured. Please contact your administrator.');
      } else {
        message.error('Failed to process Google Sheets. Please check the URL and permissions.');
      }
    } finally {
      setReading(false);
    }
  }, [googleSheetUrl, dateFormat, addLog]);

  // Preview data
  const handlePreview = useCallback(() => {
    if (!parsedData) {
      message.error('No data to preview');
      return;
    }
    setCurrentStep(2);
  }, [parsedData]);

  // Start import
  const handleImport = useCallback(async () => {
    if (!parsedData || !user.client_id) {
      message.error('No data to import or client not selected');
      return;
    }

    setImporting(true);
    setImportProgress(0);
    setImportLog([]);

    // Enable NoSleep to prevent computer from sleeping during import
    enableNoSleep();

    try {
      const token = localStorage.getItem('token');
      const api = new BulkImportApi(token);

      addLog('Starting bulk import...', 'info');

      // Archive domains once at the beginning if requested
      if (archiveDomain && domainsToArchive) {
        const domainsToArchiveArray = domainsToArchive.split(',').map((d) => d.trim()).filter((d) => d);
        if (domainsToArchiveArray.length > 0) {
          addLog('Archiving existing domains...', 'info');
          try {
            await api.archiveDomains(domainsToArchiveArray);
            addLog(`✓ Archived ${domainsToArchiveArray.length} domains`, 'success');
          } catch (error) {
            addLog(`Error archiving domains: ${error.message}`, 'error');
            message.error('Failed to archive domains. Import cancelled.');
            setImporting(false);
            return;
          }
        }
      }

      let totalItems = 0;
      let completedItems = 0;

      // Calculate total items
      parsedData.forEach((event) => {
        totalItems += 1; // event
        if (event.sessions) {
          event.sessions.forEach((session) => {
            totalItems += 1; // session
            if (session.modules) {
              session.modules.forEach((module) => {
                totalItems += 1; // module
                if (module.options) {
                  totalItems += module.options.length; // options
                }
              });
            }
          });
        }
      });

      // Import events sequentially
      const importOptionsForModule = async (options, moduleId) => {
        const importOption = async (option, optionIndex) => {
          const optionData = {
            text: option.title,
            subText: option.subtitle,
            weight: optionIndex + 1, // Use index as weight since weight is not in spreadsheet
            image: option.image || null, // Use parsed image from spreadsheet
            published: true, // Default to true since not in spreadsheet
          };

          // Only include URL if it's provided and not empty
          if (option.url && option.url.trim() !== '') {
            optionData.url = option.url;
          }

          await api.createOption(moduleId, optionData);
          completedItems += 1;
          setImportProgress(Math.floor((completedItems / totalItems) * 100));

          await delay(1500);
        };

        // Import options sequentially
        // eslint-disable-next-line no-await-in-loop
        for (let i = 0; i < options.length; i += 1) {
          await importOption(options[i], i);
        }
      };

      const importModulesForSession = async (modules, sessionId) => {
        const importModule = async (module, moduleIndex) => {
          // Map module types to the old naming convention for backwards compatibility (like v3-vcli)
          const mapToLegacyModuleTypes = (type) => {
            const trimmedString = type.trim();
            switch (trimmedString) {
              case 'banner': return 'product_launch';
              case 'chat': return 'comment';
              case 'qa': return 'questions_answers';
              case 'wordcloud': return 'word_cloud';
              case 'mood_meter': return 'moodmeter';
              case 'menu': return 'menu';
              default: return trimmedString;
            }
          };

          const moduleType = mapToLegacyModuleTypes(module.type);
          const isProductLaunch = moduleType === 'product_launch';
          const isFallback = moduleType === 'fallback';

          // Validate subtype - only include if it's one of the allowed values
          const allowedSubtypes = ['photo', 'text', 'reaction'];
          const validSubtype = module.subtype && allowedSubtypes.includes(module.subtype.toLowerCase()) 
            ? module.subtype.toLowerCase() 
            : null;

          const moduleData = {
            name: module.title,
            subtitle: module.subtitle || '',
            type: moduleType,
            ...(validSubtype && { subtype: validSubtype }), // Only include subtype if valid
            status: module.status.toUpperCase(), // Ensure status is always uppercase
            published: true, // Default to true since not in spreadsheet
            show_results: module.show_votes !== undefined ? module.show_votes : true, // Use parsed value from spreadsheet
            show_total_count: module.show_votes_count !== undefined ? module.show_votes_count : true, // Use parsed value from spreadsheet
            show_all_comments: module.show_comments !== undefined ? module.show_comments : false, // Use parsed value from spreadsheet
            show_own_comments: module.show_comments_moderated !== undefined ? module.show_comments_moderated : false, // Use parsed value from spreadsheet
            show_new_comments: true, // Default to true since not in spreadsheet
            is_paid: false, // Default to false since not in spreadsheet
            price: 0, // Default to 0 since not in spreadsheet
            megavote_price: 0, // Default to 0 since not in spreadsheet
            megavote_amount: 0, // Default to 0 since not in spreadsheet
            moodmeter_labels: [], // Default to empty array since not in spreadsheet
            collect_contact_details: module.collect_contact_details !== undefined ? module.collect_contact_details : false, // Use parsed value from spreadsheet
            contact_cta_text: module.cta || null, // Use parsed value from spreadsheet
            vote_limit: module.vote_limit !== undefined ? module.vote_limit : null, // Use parsed vote_limit from spreadsheet
            // Schedule fields with timezone conversion
            schedule_start: module.schedule_start || null,
            schedule_end: module.schedule_end || null,
            timezone: module.timezone || 'UTC',
            // For product_launch and fallback modules, store image as banner_image; others use image
            ...(isProductLaunch || isFallback ? { banner_image: module.image || null } : { image: module.image || null }),
            // For product_launch and fallback, map hyperlink to header_text and cta to cta_text
            ...((isProductLaunch || isFallback) && module.hyperlink ? { header_text: module.hyperlink } : {}),
            ...((isProductLaunch || isFallback) && module.cta ? { cta_text: module.cta } : {}),
          };

          const moduleResult = await api.createModule(sessionId, moduleData, moduleIndex);
          completedItems += 1;
          setImportProgress(Math.floor((completedItems / totalItems) * 100));
          addLog(`✓ Created module: ${module.title}`, 'success');

          // Import options for this module
          if (module.options) {
            await importOptionsForModule(module.options, moduleResult.id);
          }

          await delay(2500);
        };

        // Import modules sequentially
        // eslint-disable-next-line no-await-in-loop
        for (let i = 0; i < modules.length; i += 1) {
          await importModule(modules[i], i);
        }
      };

      const importSessionsForEvent = async (sessions, eventId) => {
        const importSession = async (session) => {
          const sessionData = {
            name: session.name,
            description: session.description || '',
            is_live: session.is_live || false, // Use parsed value from spreadsheet
            show_terms: session.show_terms || false, // Use parsed value from spreadsheet
            image: session.image || null, // Use parsed image from spreadsheet
            display_info: false, // Default to false since not in spreadsheet
            terms_location: 'welcome', // Default to welcome since not in spreadsheet
          };

          const sessionResult = await api.createSession(eventId, sessionData);
          completedItems += 1;
          setImportProgress(Math.floor((completedItems / totalItems) * 100));
          addLog(`✓ Created Session: ${session.name}`, 'success');

          // Import modules for this session
          if (session.modules) {
            await importModulesForSession(session.modules, sessionResult.id);
          }
        };

        // Import sessions sequentially
        // eslint-disable-next-line no-await-in-loop
        for (let i = 0; i < sessions.length; i += 1) {
          await importSession(sessions[i], i);
        }
      };

      const importEvent = async (event) => {
        const eventData = {
          name: event.name,
          domain_name: event.domain || `${event.name.toLowerCase().replace(/\s+/g, '-')}.votemo.com`,
          description: event.description || '',
          primary_color: event.primary_color || '1B1464',
          secondary_color: event.secondary_color || '00C9FF',
          display_header: event.display_header || false,
          display_menu: event.display_menu || false,
          display_qr: event.display_qr || false,
          display_info: false, // Default to false since not in spreadsheet
          logo: event.logo || null, // Pass the raw logo URL to backend
          track_user: false, // Default to false since not in spreadsheet
          language: 'EN', // Default to EN since not in spreadsheet
          payment_provider: 'none', // Default to none since not in spreadsheet
          start_date: toISODate(event.start_date) || null,
          end_date: toISODate(event.end_date) || null,
        };

        const eventResult = await api.createEvent(user.client_id, eventData);
        completedItems += 1;
        setImportProgress(Math.floor((completedItems / totalItems) * 100));
        addLog(`✓ Created Event: ${event.name}`, 'success');

        // Import sessions for this event
        if (event.sessions) {
          await importSessionsForEvent(event.sessions, eventResult.id);
        }
      };

      // Import events sequentially
      // eslint-disable-next-line no-await-in-loop
      for (let i = 0; i < parsedData.length; i += 1) {
        await importEvent(parsedData[i]);
      }

      addLog('Bulk import completed successfully!', 'success');
      message.success('Bulk import completed successfully!');
      setCurrentStep(3);
    } catch (error) {
      // Extract the specific GraphQL error message if available
      let errorMessage = error.message;
      if (error.response && error.response.errors && error.response.errors.length > 0) {
        const graphQLError = error.response.errors[0];
        errorMessage = graphQLError.message;
        
        // Log the full error details to console for debugging
        console.error('GraphQL Error Details:', JSON.stringify(error.response.errors, null, 2));
      }
      
      addLog(`Import failed: ${errorMessage}`, 'error');
      message.error(`Import failed: ${errorMessage}`);
    } finally {
      // Disable NoSleep when import completes (success or failure)
      disableNoSleep();
      setImporting(false);
    }
  }, [parsedData, user.client_id, addLog, archiveDomain, domainsToArchive, enableNoSleep, disableNoSleep]);

  // Helper function to convert date strings to ISO 8601
  function toISODate(dateString) {
    if (!dateString) return dateString;
    // Try moment.js if available
    if (typeof moment === 'function') {
      // Try several common formats
      const formats = [
        'DD/MM/YYYY HH:mm:ss',
        'MM/DD/YYYY HH:mm:ss',
        'YYYY-MM-DD HH:mm:ss',
        moment.ISO_8601,
      ];
      for (let i = 0; i < formats.length; i += 1) {
        const m = moment(dateString, formats[i], true);
        if (m.isValid()) return m.toISOString();
      }
    }
    // Fallback to built-in Date
    const d = new Date(dateString);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
    // Fallback: return original
    return dateString;
  }

  // Auto-populate domainsToArchive when toggling archiveDomain
  const handleArchiveDomainToggle = () => {
    const nextValue = !archiveDomain;
    setArchiveDomain(nextValue);
    if (nextValue && !domainsToArchive && autoDomainsToArchive) {
      setDomainsToArchive(autoDomainsToArchive);
    }
  };

  // Auto-populate domainsToArchive when archiveDomain is true and autoDomainsToArchive is available
  useEffect(() => {
    if (
      archiveDomain 
      && !domainsToArchive 
      && autoDomainsToArchive
    ) {
      setDomainsToArchive(autoDomainsToArchive);
    }
  }, [archiveDomain, autoDomainsToArchive, domainsToArchive]);

  // Upload props
  const uploadProps = {
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                     || file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('You can only upload Excel files!');
        return false;
      }
      handleFileUpload(file);
      return false; // Prevent default upload
    },
    fileList,
    onChange: ({ fileList: newFileList }) => setFileList(newFileList),
  };

  const steps = [
    {
      title: 'Upload Data',
      description: 'Choose your import source',
    },
    {
      title: 'Preview',
      description: 'Review parsed data',
    },
    {
      title: 'Import',
      description: 'Execute bulk import',
    },
    {
      title: 'Complete',
      description: 'Import finished',
    },
  ];

  const getLogTextType = (type) => {
    if (type === 'error') return 'danger';
    if (type === 'success') return 'success';
    if (type === 'warning') return 'warning';
    return 'secondary';
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}>Bulk Import</Title>
        <Text type="secondary">
          Import events, sessions, modules, and options from Excel files or Google Sheets.
        </Text>

        <Divider />

        <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />

        {/* Step 1: Upload Data */}
        {currentStep === 0 && (
          <Spin spinning={reading} tip="Loading Google Sheet...">
            <div>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Card title="Import Source" size="small">
                    <Row gutter={[0, 16]}>
                      <Col span={24}>
                        <Select
                          value={importSource}
                          onChange={setImportSource}
                          style={{ width: 200 }}
                        >
                          <Option value="file">Local Excel File</Option>
                          <Option value="google">Google Sheets</Option>
                        </Select>
                      </Col>
                      <Col span={24}>
                        <Select
                          value={dateFormat}
                          onChange={setDateFormat}
                          style={{ width: 200 }}
                          placeholder="Select date format"
                        >
                          {dateFormatOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Col>
                    </Row>
                  </Card>
                </Col>

                <Col span={24}>
                  {importSource === 'file' ? (
                    <Card title="Upload Excel File" size="small">
                      <Upload {...uploadProps}>
                        <Button icon="upload">Click to Upload</Button>
                      </Upload>
                      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        Supported formats: .xlsx, .xls
                      </Text>
                    </Card>
                  ) : (
                    <Card title="Google Sheets URL" size="small">
                      <Row gutter={[0, 16]}>
                        <Col span={24}>
                          <TextArea
                            value={googleSheetUrl}
                            onChange={(e) => setGoogleSheetUrl(e.target.value)}
                            placeholder="Enter Google Sheets URL (e.g., https://docs.google.com/spreadsheets/d/...)"
                            rows={3}
                          />
                        </Col>
                        <Col span={24}>
                          <Button
                            type="primary"
                            icon="google"
                            onClick={handleGoogleSheets}
                            loading={importing}
                          >
                            Process Google Sheet
                          </Button>
                        </Col>
                      </Row>
                    </Card>
                  )}
                </Col>
              </Row>
            </div>
          </Spin>
        )}

        {/* Step 2: Preview */}
        {currentStep === 1 && (
          <div>
            <Card title="Data Preview" size="small">
              {previewData && (
                <div>
                  <Alert
                    message={`Found ${previewData.length} events to import`}
                    type="info"
                    style={{ marginBottom: 16 }}
                  />

                  {previewData.map((event, index) => (
                    <Card key={`event-${event['#'] || index}-${event.name || 'unnamed'}-${event.domain || 'no-domain'}`} size="small" style={{ marginBottom: 8 }}>
                      <Text strong>{event.name}</Text>
                      <br />
                      <Text type="secondary">
                        Sessions: {event.sessions?.length || 0} |
                        Modules: {event.sessions?.reduce((total, session) => total + (session.modules?.length || 0), 0) || 0}
                      </Text>
                    </Card>
                  ))}

                  <Divider />

                  <Card title="Archive Options" size="small" style={{ marginTop: 16 }}>
                    <Row gutter={[0, 16]}>
                      <Col span={24}>
                        <Text strong>Archive Existing Domains</Text>
                        <br />
                        <Text type="secondary">
                          If enabled, existing domains will be renamed with "-ARCHIVED-YYYYMMDDHHMMSS" suffix to make room for new events with the same domain names.
                        </Text>
                      </Col>
                      <Col span={24}>
                        <Button
                          type={archiveDomain ? 'primary' : 'default'}
                          onClick={handleArchiveDomainToggle}
                        >
                          {archiveDomain ? '✓ Archive Domains Enabled' : 'Archive Domains Disabled'}
                        </Button>
                      </Col>
                      {archiveDomain && (
                        <Col span={24}>
                          <Text strong>Domains to Archive (comma-separated):</Text>
                          <br />
                          <TextArea
                            value={domainsToArchive}
                            onChange={(e) => setDomainsToArchive(e.target.value)}
                            placeholder="e.g., KinoKijow, SalaNiebieska, SalaCzerwona, KinoASP, MOS1, MOS2, MIKRO"
                            rows={3}
                            style={{ marginTop: 8 }}
                          />
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            Enter domain names to archive. Any existing domains with these names will be archived.
                          </Text>
                        </Col>
                      )}
                    </Row>
                  </Card>

                  <Card title="Sleep Prevention" size="small" style={{ marginTop: 16 }}>
                    <Row gutter={[0, 16]}>
                      <Col span={24}>
                        <Text strong>Prevent Computer Sleep</Text>
                        <br />
                        <Text type="secondary">
                          Keep your computer awake during long imports to prevent interruption.
                        </Text>
                      </Col>
                      <Col span={24}>
                        <Button
                          type={noSleepEnabled ? 'primary' : 'default'}
                          onClick={noSleepEnabled ? disableNoSleep : enableNoSleep}
                          icon={noSleepEnabled ? 'lock' : 'unlock'}
                        >
                          {noSleepEnabled ? '🔒 Wake Lock Active' : '🔓 Enable Wake Lock'}
                        </Button>
                        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                          {noSleepEnabled 
                            ? 'Your computer will not go to sleep while this page is active.'
                            : 'Click to prevent your computer from sleeping during imports.'}
                        </Text>
                      </Col>
                    </Row>
                  </Card>
                </div>
              )}

              <Row style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Button onClick={() => setCurrentStep(0)}>Back</Button>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                  <Button type="primary" onClick={handlePreview}>
                    Continue to Import
                  </Button>
                </Col>
              </Row>
            </Card>
          </div>
        )}

        {/* Step 3: Import */}
        {currentStep === 2 && (
          <div>
            <Card title="Import Progress" size="small">
              <Progress percent={importProgress} status={importing ? 'active' : 'normal'} />

              <div style={{ marginTop: 16, maxHeight: 300, overflowY: 'auto' }}>
                {importLog.map((log, index) => (
                  <div key={`log-${log.timestamp}-${index}`} style={{ marginBottom: 4 }}>
                    <Text type={getLogTextType(log.type)}>
                      {log.message}
                    </Text>
                  </div>
                ))}
              </div>

              <Row style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Button onClick={() => setCurrentStep(1)} disabled={importing}>
                    Back
                  </Button>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                  <Button
                    type="primary"
                    onClick={handleImport}
                    loading={importing}
                    disabled={importing}
                  >
                    Start Import
                  </Button>
                </Col>
              </Row>
            </Card>
          </div>
        )}

        {/* Step 4: Complete */}
        {currentStep === 3 && (
          <div>
            <Card title="Import Complete" size="small">
              <Alert
                message="Bulk import completed successfully!"
                type="success"
                style={{ marginBottom: 16 }}
              />

              <div style={{ marginTop: 16, maxHeight: 300, overflowY: 'auto' }}>
                {importLog.map((log, index) => (
                  <div key={`log-${log.timestamp}-${index}`} style={{ marginBottom: 4 }}>
                    <Text type={getLogTextType(log.type)}>
                      {log.message}
                    </Text>
                  </div>
                ))}
              </div>

              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col>
                  <Button onClick={() => window.location.reload()}>
                    Start New Import
                  </Button>
                </Col>
              </Row>
            </Card>
          </div>
        )}

        <Divider />

        <Card title="Instructions" size="small">
          <Text>
            <strong>Excel File Format:</strong>
            <br />
            Your Excel file should contain the following sheets:
            <ul>
              <li><strong>Events:</strong> #, Name, Domain Name, Start Date, End Date, Primary Color, Secondary Color, Display Header, Display Menu, Display QR, Display Info, Logo</li>
              <li><strong>Sessions:</strong> #, Event, Name, Start Date, End Date, Image</li>
              <li><strong>Modules:</strong> #, Session, Name, Type, Question, Description, Start Date, End Date, Image</li>
              <li><strong>Options:</strong> #, Module, Text, Weight, Image, URL (optional)</li>
            </ul>
            <br />
            <strong>Important Notes:</strong>
            <ul>
              <li>All column headers must have valid names. Untitled or empty columns will be skipped automatically.</li>
              <li>Make sure all required columns are present and properly named.</li>
              <li>Any warnings about skipped columns will be displayed in the import log.</li>
              <li><strong>Image URLs:</strong> Images will be automatically uploaded to the server. Both direct image URLs and Google Drive URLs are supported.</li>
              <li><strong>Module Types:</strong> Supported types include: banner, chat, qa, wordcloud, mood_meter, menu, poll, and others.</li>
              <li><strong>Menu Module:</strong> Use type "menu" for navigation menus. Options can include URLs for navigation links.</li>
              <li><strong>Option URLs:</strong> For menu modules, options can include a URL column for navigation links. This field is optional.</li>
            </ul>
            <br />
            <strong>Google Sheets (Service Account Access):</strong>
            <br />
            <ul>
              <li>Your Google Sheet must be shared with the service account email address shown in the import log.</li>
              <li>The service account needs "Viewer" access to read the spreadsheet.</li>
              <li>You can share the entire folder containing the spreadsheet for easier access management.</li>
              <li>If you get an access error, copy the service account email from the log and share the spreadsheet with that email address.</li>
            </ul>
          </Text>
        </Card>
      </Card>
    </div>
  );
}

export default BulkImportPage;
